<template>
  <b-card data-e2e="profile-skills-card">
    <div class="mb-5">
      <span class="uppercase text-gray-300 text-lg">{{ $t('profile_skills.title') }}</span>
      <button
        class="inline-flex items-center justify-center h-5 w-5 rounded-full border border-gray-500 text-gray-400 text-xs hover:border-gray-300 hover:text-gray-200 transition-colors cursor-pointer ml-2 align-middle"
        @click="showHelp = true"
      >
        ?
      </button>
    </div>

    <b-modal v-model="showHelp" :title="$t('profile_skills.help_title')" size="md" no-footer>
      <div class="px-6 pb-6">
        <p class="text-gray-300 text-sm leading-relaxed">{{ $t('profile_skills.help_description_1') }}</p>
        <p class="text-gray-300 text-sm leading-relaxed mt-3">{{ $t('profile_skills.help_description_2') }}</p>
        <div class="mt-6 flex justify-end">
          <button
            class="rounded-lg bg-indigo-600 px-4 py-2 text-sm text-white hover:bg-indigo-500 transition-colors cursor-pointer"
            @click="showHelp = false"
          >
            {{ $t('profile_skills.help_close') }}
          </button>
        </div>
      </div>
    </b-modal>

    <div v-if="loading" class="flex justify-center py-8">
      <b-loader-spinner />
    </div>

    <div v-else-if="error" class="text-red-400 text-center py-4">
      {{ $t('profile_skills.error_loading_statistics') }}
    </div>

    <div v-else-if="!statistics || (!statistics.company_sizes?.length && !statistics.deal_sizes?.length && !statistics.market_segments?.length && !statistics.industries?.length)" class="text-gray-400 text-center py-8">
      {{ $t('profile_skills.no_experience_data') }}
    </div>

    <div v-else class="relative">
      <button
        v-if="canScrollLeft"
        class="absolute left-0 top-1/2 -translate-y-1/2 z-10 flex items-center justify-center h-8 w-8 rounded-full bg-gray-800 border border-gray-600 text-gray-300 hover:bg-gray-700 hover:text-white transition-colors cursor-pointer shadow-lg"
        @click="scrollBy(-1)"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <polyline points="15 18 9 12 15 6" />
        </svg>
      </button>

      <button
        v-if="canScrollRight"
        class="absolute right-0 top-1/2 -translate-y-1/2 z-10 flex items-center justify-center h-8 w-8 rounded-full bg-gray-800 border border-gray-600 text-gray-300 hover:bg-gray-700 hover:text-white transition-colors cursor-pointer shadow-lg"
        @click="scrollBy(1)"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <polyline points="9 18 15 12 9 6" />
        </svg>
      </button>

      <div ref="scrollContainer" class="flex gap-4 overflow-x-auto scroll-smooth snap-x snap-mandatory pb-1 scrollbar-custom" @scroll="updateScrollState">
        <div data-e2e="profile-skills-industries" class="shrink-0 w-[320px] snap-start rounded-lg bg-gray-900 border border-[#373737] p-2">
          <h3 class="mb-1 text-white text-xs uppercase">{{ $t('profile_skills.industry') }}</h3>
          <b-radar-chart v-if="industryItems.length" :data="industryItems" color="#c084fc" />
          <p v-else class="text-gray-400 text-sm text-center py-4">{{ $t('profile_skills.no_data') }}</p>
        </div>

        <div data-e2e="profile-skills-market-segments" class="shrink-0 w-[320px] snap-start rounded-lg bg-gray-900 border border-[#373737] p-2">
          <h3 class="mb-1 text-white text-xs uppercase">{{ $t('profile_skills.market_segment') }}</h3>
          <b-radar-chart v-if="marketSegmentsItems.length" :data="marketSegmentsItems" color="#4ade80" />
          <p v-else class="text-gray-400 text-sm text-center py-4">{{ $t('profile_skills.no_data') }}</p>
        </div>

        <div data-e2e="profile-skills-company-sizes" class="shrink-0 w-[320px] snap-start rounded-lg bg-gray-900 border border-[#373737] p-2">
          <h3 class="mb-1 text-white text-xs uppercase">{{ $t('profile_skills.company_type') }}</h3>
          <b-radar-chart v-if="companySizesItems.length" :data="companySizesItems" color="#60a5fa" />
          <p v-else class="text-gray-400 text-sm text-center py-4">{{ $t('profile_skills.no_data') }}</p>
        </div>

        <div data-e2e="profile-skills-deal-sizes" class="shrink-0 w-[320px] snap-start rounded-lg bg-gray-900 border border-[#373737] p-2">
          <h3 class="mb-1 text-white text-xs uppercase">{{ $t('profile_skills.deal_sizes') }}</h3>
          <b-radar-chart v-if="dealSizeItems.length" :data="dealSizeItems" color="#f87171" />
          <p v-else class="text-gray-400 text-sm text-center py-4">{{ $t('profile_skills.no_data') }}</p>
        </div>
      </div>
    </div>
  </b-card>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, computed, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import BCard from "~/components/card/BCard.vue";
import BModal from '~/components/modal/BModal.vue';
import BRadarChart from '~/components/graph/BRadarChart.vue';
import BLoaderSpinner from '~/components/loader/BLoaderSpinner.vue';
import type { User } from '~/stores/userStore';
import type { ExperienceStatisticsResponse, StatisticItem } from '~/api/v2/experiences/ExperienceRepository';
import { radarDepthValue, radarWeightedCount } from '~/help/radarDepth';
import { useRadarTooltip } from '~/composables/useRadarTooltip';

const props = defineProps({
  user: {
    type: Object as PropType<User>,
    required: true,
  },
});

const { $api } = useNuxtApp();
const statistics = ref<ExperienceStatisticsResponse | null>(null);
const allCompanySizes = ref<Array<{ name: string }>>([]);
const allDealSizes = ref<Array<{ name: string }>>([]);
const allMarketSegments = ref<Array<{ name: string }>>([]);
const allIndustries = ref<Array<{ name: string }>>([]);
const loading = ref(false);
const error = ref<string | null>(null);
const showHelp = ref(false);
const skills = ref<any[]>([]);

const scrollContainer = ref<HTMLDivElement | null>(null);
const canScrollLeft = ref(false);
const canScrollRight = ref(false);

const { t } = useI18n();
const radarTooltip = useRadarTooltip();

const updateScrollState = () => {
  const el = scrollContainer.value;
  if (!el) {
    canScrollLeft.value = false;
    canScrollRight.value = false;
    return;
  }
  canScrollLeft.value = el.scrollLeft > 4;
  canScrollRight.value = el.scrollLeft + el.clientWidth < el.scrollWidth - 4;
};

const scrollBy = (direction: -1 | 1) => {
  const el = scrollContainer.value;
  if (!el) return;
  el.scrollBy({ left: direction * el.clientWidth * 0.8, behavior: 'smooth' });
};

watch([() => statistics.value, loading], async () => {
  await nextTick();
  updateScrollState();
});

const fetchStatistics = async (userId: string) => {
  loading.value = true;
  error.value = null;
  try {
    const [statsResponse, skillsResponse, companySizes, dealSizes, marketSegments, industries] = await Promise.all([
      $api().v2.experiences.getStatistics(userId),
      $api().v2.skills.list({ 'filter[user_id]': userId }),
      $api().v2.companySizes.list(),
      $api().v2.dealSizes.list(),
      $api().v2.marketSegments.list(),
      $api().v2.industries.list(),
    ]);
    statistics.value = statsResponse;
    skills.value = skillsResponse || [];
    allCompanySizes.value = companySizes || [];
    allDealSizes.value = dealSizes || [];
    allMarketSegments.value = marketSegments || [];
    allIndustries.value = industries || [];
  } catch (e) {
    console.error('Failed to fetch data:', e);
    error.value = 'Failed to load statistics.';
  } finally {
    loading.value = false;
  }
};

const RADAR_POINTS = 5;

function topWithPadding(
  stats: StatisticItem[] | undefined,
  allItems: Array<{ name: string }>,
): Array<{ label: string; value: number; tooltip: string }> {
  const list = stats || [];
  if (list.length === 0) return [];

  // Top 5 par profondeur pondérée (validée = 1, non validée = 0,5), pas par count brut.
  const sorted = [...list].sort((a, b) => radarWeightedCount(b) - radarWeightedCount(a));
  const top = sorted.slice(0, RADAR_POINTS);
  const items = top.map((item) => ({
    label: item.name,
    value: radarDepthValue(item),
    tooltip: radarTooltip(item),
  }));

  if (items.length >= RADAR_POINTS) return items;

  // Padding stable (alphabétique) : le radar d'un profil doit être reproductible.
  const existingNames = new Set(top.map((s) => s.name));
  const padding = allItems
    .filter((i) => !existingNames.has(i.name))
    .sort((a, b) => a.name.localeCompare(b.name))
    .slice(0, RADAR_POINTS - items.length)
    .map((i) => ({
      label: i.name,
      value: 0,
      tooltip: t('profile_skills.tooltip_no_experience'),
    }));

  return [...items, ...padding];
}

const companySizesItems = computed(() =>
  topWithPadding(statistics.value?.company_sizes, allCompanySizes.value)
);

const dealSizeItems = computed(() =>
  topWithPadding(statistics.value?.deal_sizes, allDealSizes.value)
);

const marketSegmentsItems = computed(() =>
  topWithPadding(statistics.value?.market_segments, allMarketSegments.value)
);

const industryItems = computed(() =>
  topWithPadding(statistics.value?.industries, allIndustries.value)
);

onMounted(() => {
  if (props.user?.id) {
    fetchStatistics(props.user.id);
  }
  window.addEventListener('resize', updateScrollState);
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', updateScrollState);
});

watch(() => props.user, (newUser) => {
  if (newUser?.id) {
    fetchStatistics(newUser.id);
  }
}, { immediate: false });
</script>
